import { Link } from 'react-router-dom';
import { SectionHeader } from '@/components/ui';
import type { SaleHistoryEntry } from '@/seo/brandHubs';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import type { Locale } from '@/seo/locales';
import { formatDayMonthYear } from '@/seo/text';

/**
 * Rows rendered (most recent first). The hub can list 100; each row is in the
 * HTML twice (markup and hydration data), so the table stops here, the same on
 * the server and in the browser (no "show more": older sales are not listed).
 */
export const SALE_HISTORY_MAX_ROWS = 20;

export interface SaleHistoryTableProps {
  /** Brand display name. */
  brandName: string;
  /** Sale history rows, most recently ended first. */
  history: readonly SaleHistoryEntry[];
  className?: string;
}

/**
 * Format a date string (ISO or "YYYY-MM-DD") to a short human label ("1 Jun
 * 2024"): the day in the market's time zone with fixed month names, so the
 * server-rendered table (any server zone, any ICU) and the browser's render
 * that hydrates it print the same text.
 */
function formatDate(raw: string | null | undefined, locale: Locale): string {
  if (!raw) return '—';
  return formatDayMonthYear(raw, locale.timeZone) || raw;
}

/**
 * Sale history table for the brand hub (web plan W3.6).
 *
 * Each row shows the sale's headline, date range and max discount. The row
 * title is a link to the promotion page only while `urlPath` is non-null
 * (≤ 90 days after end). After that the entry is plain text so there are
 * never any dead links to 410 pages.
 */
export function SaleHistoryTable({ brandName, history, className }: SaleHistoryTableProps) {
  const locale = useRouteLocale();
  if (history.length === 0) return null;
  const title = `${brandName} Sale History`;
  const rows = history.slice(0, SALE_HISTORY_MAX_ROWS);

  return (
    <section aria-label={title} className={className}>
      <SectionHeader title={title} />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-tertiary">
              <th scope="col" className="pb-2 pr-4">Sale</th>
              <th scope="col" className="pb-2 pr-4">Dates</th>
              <th scope="col" className="pb-2">Max Discount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const headline = entry.headline ?? 'Sale';
              const dateRange = `${formatDate(entry.startDate, locale)} – ${formatDate(entry.endDate, locale)}`;
              const discount =
                entry.discountMax != null ? `Up to ${entry.discountMax}% off` : '—';

              return (
                <tr
                  key={entry.id}
                  className="border-b border-line/50 last:border-0"
                >
                  <td className="py-2.5 pr-4 font-medium text-ink">
                    {/* Only link while the promotion page is still alive (≤ 90d). */}
                    {entry.urlPath ? (
                      <Link
                        to={entry.urlPath}
                        className="hover:text-primary hover:underline"
                      >
                        {headline}
                      </Link>
                    ) : (
                      <span>{headline}</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-ink-secondary">{dateRange}</td>
                  <td className="py-2.5 text-ink-secondary">{discount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default SaleHistoryTable;
